import { useCallback } from 'react';
import { ActionType, ItemType } from '../types/messaging-types';
import { getBrowserLogger } from '../browser-logger';
import { LogLevel, PageEventType } from '../browser-logger/types';
import { JobEntity, ConversationFolderLabel } from '@indeed/messaging-react';
import { channelOnlineStatusesVar } from '../utils/onlineStatusUtils';
import { getUrlParam } from '../utils/urlUtils';

// Define constants to match the original implementation
enum OutLinkCause {
    COMPANY_FIELD_IN_DETAIL_PANE = 'COMPANY_FIELD_IN_DETAIL_PANE',
    JOB_LINK_IN_DETAIL_PANE = 'JOB_LINK_IN_DETAIL_PANE',
}

enum OutLinkType {
    LINK_TO_COMPANYPAGE = 'LINK_TO_COMPANYPAGE',
    LINK_TO_JOBPAGE = 'LINK_TO_JOBPAGE',
}

// Constants for JobInfoCard
export const JOB_INFO_CARD_IMPRESSION_UID = 'conversation-job-info-card';

/**
 * Custom hook that provides logging utilities for messaging-react components.
 * This hook wraps the BrowserLogger methods to provide callback functions
 * that can be passed as props to messaging-react components.
 *
 * Based on the design document, this maintains separation of concerns where
 * messaging-react components only define what to log and when to log,
 * while messaging-webapp implements how logging works.
 *
 * ⚠️  IMPORTANT: The specific callback functions defined in this utility are
 * temporary and subject to change as the messaging-react rearchitecture progresses.
 * The function signatures, parameters, and logging behavior may be modified
 * based on the actual requirements of the messaging-react components when they
 * become available.
 *
 * The overall design pattern (callback-based logging) will remain stable,
 * but individual logging functions should be considered experimental until
 * the rearchitecture is complete.
 */
export const useLogging = () => {
    // Try to get the browser logger, and create a fallback logger if it's not available
    let browserLogger;
    try {
        browserLogger = getBrowserLogger();
    } catch (error) {
        // Create a fallback logger with no-op methods
        console.warn('Browser logger not initialized yet. Using fallback logger.', error);
        browserLogger = {
            logConversationImpression: () => {},
            logJobDetailImpression: () => {},
            logButtonImpression: () => {},
            logUserAction: () => {},
            logPageEvent: () => {},
            logCaughtError: () => {},
            logMessage: () => {},
            logImpression: () => {},
        };
    }

    const logConversationImpression = useCallback(
        (conversationSid: string) => {
            try {
                const log = {
                    conversationSids: [conversationSid],
                    // TODO: The folder and other data needs to be corrected.
                    folders: ['inbox'],
                    hasCompanyName: [false],
                    hasTitleLink: [false],
                    isArchived: [false],
                    isInitiated: [true],
                    isJobRemoved: [false],
                    isOld: [false],
                    lastEventId: [''],
                    lastEventType: [''],
                    positions: [0],
                    reminders: [false],
                    requireResponse: [false],
                    types: [''],
                    unreadCounts: [0],
                    isPreApply: [false],
                    jobKey: [''],
                };
                browserLogger.logConversationImpression(log);
            } catch (error) {
                console.warn('Failed to log conversation impression:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs conversation list impressions
     * Used for onRender callbacks in ConversationList components
     */
    const logConversationListImpression = useCallback(
        (conversationIds: string[], folder: string) => {
            if (conversationIds.length === 0) return;

            const log = {
                conversationSids: conversationIds,
                folders: conversationIds.map(() => folder),
                hasCompanyName: conversationIds.map(() => false),
                hasTitleLink: conversationIds.map(() => false),
                isArchived: conversationIds.map(() => false),
                isInitiated: conversationIds.map(() => true),
                isJobRemoved: conversationIds.map(() => false),
                isOld: conversationIds.map(() => false),
                lastEventId: conversationIds.map(() => ''),
                lastEventType: conversationIds.map(() => ''),
                positions: conversationIds.map((_, i) => i),
                reminders: conversationIds.map(() => false),
                requireResponse: conversationIds.map(() => false),
                types: conversationIds.map(() => ''),
                unreadCounts: conversationIds.map(() => 0),
                isPreApply: conversationIds.map(() => false),
                jobKey: conversationIds.map(() => ''),
            };

            try {
                browserLogger.logConversationImpression(log);
            } catch (error) {
                console.warn('Failed to log conversation impression:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs job detail impressions
     * Used for onJobDetailsLoaded callbacks in JobDetailCard components
     *
     * @param conversationId - The conversation ID to log impressions for
     * @param jobEntity - The JobEntity object containing job details.
     */
    const logJobDetailImpressions = useCallback(
        (conversationId: string, jobEntity: JobEntity) => {
            if (!conversationId || !jobEntity) return;

            const log = {
                conversationSids: [conversationId],
                hasCompanyName: [!!jobEntity.advertiser],
                hasCompanyPageURL: [!!jobEntity.companyPageUrl],
                hasJobSetting: [
                    Array.isArray(jobEntity.jobSetting) && jobEntity.jobSetting.length > 0,
                ],
                hasJobShift: [Array.isArray(jobEntity.jobShift) && jobEntity.jobShift.length > 0],
                hasJobTitle: [!!jobEntity.jobTitle],
                hasJobType: [Array.isArray(jobEntity.jobType) && jobEntity.jobType.length > 0],
                hasJobUrl: [!!jobEntity.jobUrl],
                hasLocation: [!!jobEntity.location],
                hasLogoUrl: [!!jobEntity.logoUrl],
                hasRating: [!!jobEntity.rating],
                jobKey: [jobEntity.jobKey || ''],
            };

            try {
                browserLogger.logJobDetailImpression(log);
            } catch (error) {
                console.warn('Failed to log job detail impression:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs job info card impressions
     * Used for onRender callbacks in JobInfoCard components
     *
     * @param conversationId - The conversation ID to log impressions for
     */
    const logJobInfoCardImpression = useCallback(
        (conversationId: string) => {
            if (!conversationId) return;

            try {
                void browserLogger.logImpression({
                    itemType: ItemType.JOB_DETAIL,
                    itemUids: [JOB_INFO_CARD_IMPRESSION_UID],
                    itemsAttributes: {},
                    additionalParams: {
                        conversationSid: conversationId,
                    },
                });
            } catch (error) {
                console.warn('Failed to log job info card impression:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs pagination button clicks
     * Used for onLoadMoreClick callbacks in ConversationList components
     */
    const logPaginationButtonClick = useCallback(() => {
        try {
            void browserLogger.logUserAction({
                itemType: ItemType.BUTTON,
                itemUid: 'VIEW_MORE_MESSAGES_CONV_LIST',
                action: ActionType.CLICK,
            });
        } catch (error) {
            console.warn('Failed to log pagination button click:', error);
        }
    }, [browserLogger]);

    /**
     * Logs conversation selection
     * Used for onConversationSelect callbacks in ConversationList components
     */
    const logConversationSelect = useCallback(
        (conversationId: string) => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.CONVERSATIONS,
                    itemUid: conversationId,
                    action: ActionType.SELECT,
                    additionalParams: {},
                });
            } catch (error) {
                console.warn('Failed to log conversation select:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs folder selection
     * Used for onFilterChange callbacks in ConversationList components
     * Maps to SELECT_FOLDER Redux action
     */
    const logFolderSelect = useCallback(
        (folder: string) => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.FOLDERS,
                    itemUid: folder.toUpperCase(),
                    action: ActionType.SELECT,
                    additionalParams: {},
                });
            } catch (error) {
                console.warn('Failed to log folder select:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs click to company page
     * Used for onCompanyClick callbacks in JobDetailCard components
     */
    const logClickToCompanyPage = useCallback(
        (conversationId?: string, companyPageUrl?: string) => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.OUTBOUND_LINK,
                    itemUid: companyPageUrl || 'COMPANY_PAGE_LINK',
                    action: ActionType.CLICK,
                    additionalParams: {
                        conversationSid: conversationId || '',
                        cause: OutLinkCause.COMPANY_FIELD_IN_DETAIL_PANE,
                        linkType: OutLinkType.LINK_TO_COMPANYPAGE,
                    },
                });
            } catch (error) {
                console.warn('Failed to log click to company page:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs click to job page
     * Used for onJobClick callbacks in JobDetailCard components
     */
    const logClickToJobPage = useCallback(
        (conversationId?: string, jobUrl?: string) => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.OUTBOUND_LINK,
                    itemUid: jobUrl || 'JOB_LINK',
                    action: ActionType.CLICK,
                    additionalParams: {
                        conversationSid: conversationId || '',
                        cause: OutLinkCause.JOB_LINK_IN_DETAIL_PANE,
                        linkType: OutLinkType.LINK_TO_JOBPAGE,
                    },
                });
            } catch (error) {
                console.warn('Failed to log click to job page:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs click to job description modal
     * Used for onDescriptionModalClick callbacks in JobDetailCard components
     */
    const logClickToJobDescriptionModal = useCallback(
        (conversationId?: string) => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.CONVERSATIONS,
                    itemUid: conversationId || 'JOB_DESCRIPTION_MODAL',
                    action: ActionType.OPEN_REZCON_JOB_DESCRIPTION,
                    additionalParams: {},
                });
            } catch (error) {
                console.warn('Failed to log click to job description modal:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs page events
     * Used for various page lifecycle events
     */
    const logPageEvent = useCallback(
        (eventType: PageEventType) => {
            try {
                browserLogger.logPageEvent(eventType);
            } catch (error) {
                console.warn('Failed to log page event:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs send message success action
     * Maps exactly to SEND_MESSAGE_SUCCESS Redux action
     * Used for onSendMessageSuccess callbacks in message compose components
     */
    const logSendMessageSuccess = useCallback(
        (messageSid: string, conversationId: string, fileCount: number = 0) => {
            try {
                // Skip logging if it's a moderated message (matches Redux logic)
                if (messageSid.includes('Moderated')) {
                    return;
                }

                const channelOnlineStatuses = channelOnlineStatusesVar();
                const isEmployerOnline = channelOnlineStatuses[conversationId] ?? false;
                const from = getUrlParam('from');

                void browserLogger.logUserAction({
                    itemType: ItemType.MESSAGES,
                    itemUid: messageSid,
                    action: ActionType.SEND,
                    additionalParams: {
                        conversationSid: conversationId,
                        fileCount,
                        isEmployerOnline,
                        from,
                    },
                });
            } catch (error) {
                console.warn('Failed to log send message success:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs retry send message action
     * Maps exactly to RETRY_SEND_MESSAGE_REQUEST Redux action
     */
    const logRetrySendMessage = useCallback(
        (messageSid: string, conversationId: string) => {
            try {
                const channelOnlineStatuses = channelOnlineStatusesVar();
                const isEmployerOnline = channelOnlineStatuses[conversationId] ?? false;
                const from = getUrlParam('from');

                void browserLogger.logUserAction({
                    itemType: ItemType.MESSAGES,
                    itemUid: messageSid,
                    action: ActionType.SEND,
                    additionalParams: {
                        conversationSid: conversationId,
                        isEmployerOnline,
                        from,
                    },
                });
            } catch (error) {
                console.warn('Failed to log retry send message:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs attachment download
     * Used for onAttachmentDownload callbacks in attachment components
     */
    const logAttachmentDownload = useCallback(
        (attachmentId: string, conversationId?: string) => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.MESSAGE_ATTACHMENT,
                    itemUid: attachmentId,
                    action: ActionType.DOWNLOAD,
                    additionalParams: {
                        conversationSid: conversationId || 'unknown',
                    },
                });
            } catch (error) {
                console.warn('Failed to log attachment download:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs conversation archive action
     * Maps to UPDATING_CONVERSATION_LABEL Redux action with ActionType.ARCHIVE
     */
    const logConversationArchive = useCallback(
        (conversationId: string, cause: string = 'conversationMenu') => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.CONVERSATIONS,
                    itemUid: conversationId,
                    action: ActionType.ARCHIVE,
                    additionalParams: {
                        cause,
                    },
                });
            } catch (error) {
                console.warn('Failed to log conversation archive:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs conversation unarchive/restore action
     * Maps to UPDATING_CONVERSATION_LABEL Redux action with ActionType.UNARCHIVE
     */
    const logConversationUnarchive = useCallback(
        (conversationId: string, cause: string = 'conversationMenu') => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.CONVERSATIONS,
                    itemUid: conversationId,
                    action: ActionType.UNARCHIVE,
                    additionalParams: {
                        cause,
                    },
                });
            } catch (error) {
                console.warn('Failed to log conversation unarchive:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs opening of report content modal
     * Maps to OPEN_REPORT_CONTENT_MODAL Redux action
     */
    const logOpenReportContentModal = useCallback(
        (conversationId: string, cause: string = 'conversationMenu') => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.CONVERSATIONS,
                    itemUid: conversationId,
                    action: ActionType.OPEN_MODERATION_REQUEST,
                    additionalParams: {
                        cause,
                    },
                });
            } catch (error) {
                console.warn('Failed to log open report content modal:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs opening of conversation detail pane
     * Maps to LOG_DETAIL_PANE_OPEN Redux action
     */
    const logOpenDetailPane = useCallback(
        (conversationId: string, cause: string = 'headerClick') => {
            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.CONVERSATIONS,
                    itemUid: conversationId,
                    action: ActionType.OPEN_DETAIL_PANE,
                    additionalParams: {
                        cause,
                    },
                });
            } catch (error) {
                console.warn('Failed to log open detail pane:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs errors that occur during user actions
     * Used for onError callbacks throughout the application
     */
    const logActionError = useCallback(
        (error: Error, context: string) => {
            try {
                browserLogger.logCaughtError(error);
                browserLogger.logMessage(
                    LogLevel.ERROR,
                    `Action error in ${context}: ${error.message}`
                );
            } catch (logError) {
                console.error('Failed to log action error:', logError);
                console.error('Original error:', error, 'Context:', context);
            }
        },
        [browserLogger]
    );

    /**
     * Logs timeline module rendering events
     */
    const logTimelineModuleRender = useCallback(
        (moduleName: string, conversationId?: string) => {
            try {
                void browserLogger.logUserAction({
                    itemType: 'TIMELINE_MODULE' as any,
                    itemUid: moduleName,
                    action: 'RENDER' as any,
                    additionalParams: {
                        conversationSid: conversationId,
                    },
                });
            } catch (error) {
                console.warn('Failed to log timeline module render:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs timeline module API call events
     */
    const logTimelineModuleApiCall = useCallback(
        (moduleName: string, statusCode: string, conversationId?: string) => {
            try {
                void browserLogger.logUserAction({
                    itemType: 'TIMELINE_MODULE' as any,
                    itemUid: moduleName,
                    action: 'API_CALL' as any,
                    additionalParams: {
                        conversationSid: conversationId,
                        statusCode: statusCode,
                    } as any,
                });
            } catch (error) {
                console.warn('Failed to log timeline module API call:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs timeline module click events
     */
    const logTimelineModuleClick = useCallback(
        (moduleName: string, conversationId?: string) => {
            try {
                void browserLogger.logUserAction({
                    itemType: 'TIMELINE_MODULE' as any,
                    itemUid: moduleName,
                    action: ActionType.CLICK,
                    additionalParams: {
                        conversationSid: conversationId,
                    },
                });
            } catch (error) {
                console.warn('Failed to log timeline module click:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs button impressions
     * Used for onRender callbacks in various button components
     */
    const logButtonImpression = useCallback(
        (buttonNames: string[]) => {
            if (buttonNames.length === 0) return;
            try {
                browserLogger.logButtonImpression(buttonNames);
            } catch (error) {
                console.warn('Failed to log button impression:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs AI button impressions
     */
    const logAiButtonImpression = useCallback(
        (conversationId: string, buttonState: string, status?: string | undefined) => {
            if (!conversationId || !buttonState) return;

            try {
                void browserLogger.logImpression({
                    itemType: ItemType.AI_ASSISTED_MESSAGING,
                    itemUids: [buttonState],
                    itemsAttributes: {},
                    additionalParams: {
                        conversationSid: conversationId,
                        ...(status && { status }),
                    },
                });
            } catch (error) {
                console.warn('Failed to log AI button impression:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs AI button clicks
     * Uses ItemType.MESSAGES to keep consistent with button click query
     */
    const logAiButtonClick = useCallback(
        (conversationId: string, buttonState: string) => {
            if (!conversationId || !buttonState) return;

            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.MESSAGES,
                    itemUid: buttonState,
                    action: ActionType.CLICK,
                    additionalParams: {
                        conversationSid: conversationId,
                    },
                });
            } catch (error) {
                console.warn('Failed to log AI button click:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs AI suggestion selection
     */
    const logAiSuggestionSelected = useCallback(
        (conversationId: string, index: number, trackingKey?: string) => {
            if (!conversationId) return;

            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.AI_ASSISTED_MESSAGING,
                    itemUid: index.toString(),
                    action: ActionType.SELECT,
                    additionalParams: {
                        conversationSid: conversationId,
                        ...(trackingKey && { aiTrackingKey: trackingKey }),
                    },
                });
            } catch (error) {
                console.warn('Failed to log AI suggestion selected:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs AI suggestions dismissal
     */
    const logAiSuggestionsDismissed = useCallback(
        (conversationId: string, trackingKey?: string) => {
            if (!conversationId) return;

            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.AI_ASSISTED_MESSAGING,
                    itemUid: 'ai-suggestions-close',
                    action: ActionType.DISMISS,
                    additionalParams: {
                        conversationSid: conversationId,
                        ...(trackingKey && { aiTrackingKey: trackingKey }),
                    },
                });
            } catch (error) {
                console.warn('Failed to log AI suggestions dismissed:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Logs AI message send action
     * Used when sending a message that was generated or modified using AI suggestions
     */
    const logAiMessageSend = useCallback(
        (
            conversationId: string,
            messageId: string,
            editDistance: number,
            index: number,
            trackingKey?: string
        ) => {
            if (!conversationId || !messageId) return;

            try {
                void browserLogger.logUserAction({
                    itemType: ItemType.AI_ASSISTED_MESSAGING,
                    itemUid: index.toString(),
                    action: ActionType.SEND,
                    additionalParams: {
                        messageId,
                        conversationSid: conversationId,
                        suggestedMessageEditDistance: editDistance,
                        ...(trackingKey && { aiTrackingKey: trackingKey }),
                    },
                });
            } catch (error) {
                console.warn('Failed to log AI message send:', error);
            }
        },
        [browserLogger]
    );

    /**
     * Helper function to build conversation log structure
     * Used by both transient and persisted conversation logging
     */
    const buildConversationLog = useCallback(
        (
            conversationSid: string | null,
            metadata: {
                jobKey?: string;
                isPreApplyConversation?: boolean;
                employerInfo?: { fullName?: string; role?: string };
                advertiserKey?: string;
                candidateKey?: string;
            }
        ) => {
            return {
                conversationSids: [conversationSid || ''],
                folders: [ConversationFolderLabel.Inbox],
                hasCompanyName: [!!metadata.employerInfo?.fullName],
                hasTitleLink: [false], // Job URL not available in metadata
                isArchived: [false],
                isInitiated: [!!conversationSid],
                isJobRemoved: [false],
                isOld: [false],
                lastEventId: [''],
                lastEventType: [''],
                positions: [0],
                reminders: [false],
                requireResponse: [false],
                types: [''],
                unreadCounts: [0],
                isPreApply: [!!metadata.isPreApplyConversation],
                jobKey: [metadata.jobKey || ''],
            };
        },
        []
    );

    /**
     * Logs transient conversation creation (PAJIM new conversation)
     * Used when user initiates a new conversation that doesn't exist yet
     */
    const logPajimConversation = useCallback(
        (
            conversationSid: string | null,
            metadata: {
                jobKey?: string;
                isPreApplyConversation?: boolean;
                employerInfo?: { fullName?: string; role?: string };
                advertiserKey?: string;
                candidateKey?: string;
            }
        ) => {
            if (!browserLogger) return;

            try {
                // isTransient: true maps to isInitiated: false
                const conversationLog = buildConversationLog(conversationSid, metadata);
                browserLogger.logConversationImpression(conversationLog);
            } catch (error) {
                console.warn('Failed to log transient conversation:', error);
            }
        },
        [browserLogger, buildConversationLog]
    );

    return {
        // Impression logging
        logConversationImpression,
        logConversationListImpression,
        logJobDetailImpressions,
        logJobInfoCardImpression,
        logButtonImpression,

        // AI logging functions
        logAiButtonImpression,
        logAiButtonClick,
        logAiSuggestionSelected,
        logAiSuggestionsDismissed,
        logAiMessageSend,

        // Conversation lifecycle logging
        logPajimConversation,

        // User action logging
        logPaginationButtonClick,
        logConversationSelect,
        logFolderSelect,
        logClickToCompanyPage,
        logClickToJobPage,
        logClickToJobDescriptionModal,
        logSendMessageSuccess,
        logRetrySendMessage,
        logAttachmentDownload,

        // Conversation folder actions
        logConversationArchive,
        logConversationUnarchive,
        logOpenReportContentModal,
        logOpenDetailPane,

        // Timeline module events
        logTimelineModuleRender,
        logTimelineModuleApiCall,
        logTimelineModuleClick,

        // Page event logging
        logPageEvent,

        // Error logging
        logActionError,
    };
};
